import React, { useEffect, useState } from 'react';
import { Box, LinearProgress, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Check, Close } from '@mui/icons-material';

// Password requirements
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'At least one uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'At least one lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'At least one number', test: (pw) => /[0-9]/.test(pw) },
  { id: 'special', label: 'At least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)', test: (pw) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw) },
];

// Calculate password strength (0-100)
const calculateStrength = (password) => {
  if (!password) return 0;
  
  let score = 0;
  
  // Length score (up to 30 points)
  score += Math.min(password.length * 3, 30);
  
  // Character variety (up to 40 points)
  if (/[a-z]/.test(password)) score += 10;
  if (/[A-Z]/.test(password)) score += 10;
  if (/[0-9]/.test(password)) score += 10;
  if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) score += 10;
  
  // Bonus for mixing (up to 30 points)
  const uniqueChars = new Set(password).size;
  score += Math.min(uniqueChars * 2, 30);
  
  return Math.min(score, 100);
};

// Get strength label and color
const getStrengthInfo = (score) => {
  if (score < 25) return { label: 'Weak', color: 'error' };
  if (score < 50) return { label: 'Fair', color: 'warning' };
  if (score < 75) return { label: 'Good', color: 'info' };
  return { label: 'Strong', color: 'success' };
};

function PasswordStrengthIndicator({ password, showRequirements = true, compact = false }) {
  const [strength, setStrength] = useState(0);
  const [requirements, setRequirements] = useState(PASSWORD_REQUIREMENTS.map(r => ({ ...r, met: false })));

  useEffect(() => {
    setStrength(calculateStrength(password || ''));
    setRequirements(PASSWORD_REQUIREMENTS.map(r => ({ ...r, met: r.test(password || '') })));
  }, [password]);

  const strengthInfo = getStrengthInfo(strength);
  const allRequirementsMet = requirements.every(r => r.met);

  if (compact) {
    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Password Strength
          </Typography>
          <Typography variant="caption" color={`${strengthInfo.color}.main`}>
            {password ? strengthInfo.label : ''}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={strength}
          color={strengthInfo.color}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Password Strength
        </Typography>
        <Typography variant="body2" color={`${strengthInfo.color}.main`} fontWeight="medium">
          {password ? strengthInfo.label : ''}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={strength}
        color={strengthInfo.color}
        sx={{ height: 8, borderRadius: 1 }}
      />
      
      {showRequirements && (
        <List dense sx={{ mt: 1, py: 0 }}>
          {requirements.map((req) => (
            <ListItem key={req.id} sx={{ py: 0, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {req.met ? (
                  <Check fontSize="small" color="success" />
                ) : (
                  <Close fontSize="small" color="error" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={req.label}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: req.met ? 'text.secondary' : 'text.primary'
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
      
      {allRequirementsMet && (
        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
          ✓ Password meets all requirements
        </Typography>
      )}
    </Box>
  );
}

// Export helper function for validation
export const isPasswordValid = (password) => {
  return PASSWORD_REQUIREMENTS.every(r => r.test(password || ''));
};

export const getPasswordErrors = (password) => {
  return PASSWORD_REQUIREMENTS.filter(r => !r.test(password || '')).map(r => r.label);
};

export default PasswordStrengthIndicator;
